import { useEffect, useState, createContext } from "react";

import { useApi } from "./useApi";
import { useAuth } from "./useAuth";

const MemberContext = createContext([{}, () => {}]);

const MemberContextProvider = (props) => {
  const [memberId, setMemberId] = useState("");
  const [memberData, setMemberData] = useState({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [customerHref, setCustomerHref] = useState(false);
  const [distributorData, setDistributorData] = useState({});
  const [customerHrefData, setCustomerHrefData] = useState({});
  const [customerOrdersLength, setCustomerOrdersLength] = useState(0);
  // customerOrdersInitialLength is used for the order count chip in the UI, neccesary because of how we're using offset=11 to determine if there are more orders
  const [customerOrdersInitialLength, setCustomerOrdersInitialLength] =
    useState(0);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [customerOrdersOffset, setCustomerOrdersOffset] = useState(0);
  const [customerOrdersOffsetLoading, setCustomerOrdersOffsetLoading] =
    useState(false);
  const [allOrdersShown, setAllOrdersShown] = useState(false);
  const [customerReferrals, setCustomerReferrals] = useState([]);
  const [customerNotes, setCustomerNotes] = useState([]);
  const [isDistributorLevelLimit, setIsDistributorLevelLimit] = useState(false);

  const [apiChainInProgress, setApiChainInProgress] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const { userInfo } = useAuth();
  const { sendRequest } = useApi();

  const clearErrorMessage = () => {
    setErrorMessage("");
  };

  const processAndSortOrders = (offsetOrders) => {
    const ordersSorted = offsetOrders.sort(
      (a, b) => b?.dateCreated?.localeCompare(a?.dateCreated) || 0
    );

    // remove duplicate orders due to issues with offset from API
    const uniqueOrders = Object.values(
      ordersSorted.reduce((acc, order) => {
        acc[order?.id?.unicity] = order;
        return acc;
      }, {})
    );

    const uniqueOrdersWithIds = uniqueOrders.map((item, idx) => {
      return { uniqueId: idx, ...item };
    });

    return uniqueOrdersWithIds;
  };

  //when unicity id is present in the route, make a call to get the customer href
  useEffect(() => {
    const abortController = new AbortController();

    if (memberId) {
      // when unicityId in URL changes, clear all member data
      setCustomerHref(false);
      setMemberData(false);
      setCustomerHrefData(false);
      setCustomerNotes(false);
      setCustomerOrders([]);
      setAllOrdersShown(false);
      // GO-2724: previously only cleared when the drawer closes (see the
      // isDrawerOpen effect below) -- switching to a different member
      // without closing the drawer (e.g. profileViewStack back-navigation)
      // carried the PREVIOUS member's error forward, incorrectly showing
      // MemberErrorScreen for a member whose own fetch may succeed fine.
      setErrorMessage("");

      setApiChainInProgress(true);

      // Check if we have prefetched data in location state
      // Check if customer data was prefetched (from Rank Qualifications optimization)
      // This happens when opening drawer from RankQualBox component
      const prefetchedData = window.history.state?.usr?.customerData;

      if (prefetchedData) {
        // Use the prefetched customer data to avoid redundant API call
        // This includes basic customer info: name, id, profile picture, sponsor
        setMemberData(prefetchedData);
        setCustomerHrefData(prefetchedData);
        
        // IMPORTANT: Even with prefetched customer data, we still need to fetch
        // auxiliary data (orders, referrals, notes) that wasn't included in the prefetch.
        // This ensures the Referral and Orders buttons work correctly.
        
        // Promise 2: Fetch customer orders (first 11 to determine if there are more)
        // Used for the Orders button and count display
        const promise2 = sendRequest({
          method: "get",
          addPrefix: false,
          endpoint: `${prefetchedData.href}/orders?expand=order&limit=11&offset=${customerOrdersOffset}`,
          abortController,
        }).catch((error) => {
          setErrorMessage(error.message);
        });

        // Promise 3: Fetch customer referrals (members sponsored by this customer)
        // Used for the Referrals button and count display
        const promise3 = sendRequest({
          method: "get",
          addPrefix: false,
          endpoint: `${process.env.REACT_APP_OFFICE_API}/${userInfo.unicityId}/members?sponsorUnicityId=${memberId}`,
          abortController,
        }).catch((err) => console.log(err));

        // Promise 4: Fetch notes associated with this customer
        // Used for the Notes section at the bottom of the drawer
        const promise4 = sendRequest({
          method: "get",
          addPrefix: false,
          endpoint: `${process.env.REACT_APP_OFFICE_API}/${userInfo.unicityId}/notes?customerId=${memberId}`,
          abortController,
        }).catch((err) => console.log(err));

        // Promise 5: Check if this customer is at the distributor level limit
        // Used to determine if certain actions should be restricted
        const promise5 = sendRequest({
          method: "get",
          addPrefix: false,
          endpoint: `${process.env.REACT_APP_OFFICE_API}/${userInfo.unicityId}/distributorLevelLimit?partnerId=${memberId}`,
          abortController,
        }).catch((err) => console.log(err));

        // Promise 6: Fetch accurate order count from search endpoint
        // Used to display the exact number of orders (more reliable than promise2 length)
        const promise6 = sendRequest({
          method: "get",
          addPrefix: false,
          endpoint: `${process.env.REACT_APP_OFFICE_API}/${userInfo.unicityId}/members?searchTerm=${memberId}&limit=1&sortField=relevance&sortDirection=desc`,
          abortController,
        }).catch((err) => console.log(err));

        // Wait for all auxiliary data requests to complete
        Promise.all([promise2, promise3, promise4, promise5, promise6]).then((values) => {
          try {
            // values[0] = orders response (promise2)
            // values[1] = referrals response (promise3)
            // values[2] = notes response (promise4)
            // values[3] = distributor level limit response (promise5)
            // values[4] = member search response with order count (promise6)
            
            // Set the accurate order count from the search endpoint (promise6 result)
            setCustomerOrdersLength(values[4]?.data?.items[0]?.orders);
            
            // Process orders data from promise2
            const items = values[0] !== undefined ? values[0].data.items : [];
            if (items.length < 11) {
              setAllOrdersShown(true);
            }
            
            // If less than 11 orders returned, we've fetched all available orders
            if (items.length < 11) setAllOrdersShown(true);
            
            // Store initial order count before processing
            setCustomerOrdersInitialLength(items.length);
            
            // Sort orders and remove duplicates
            const ordersProcessed = processAndSortOrders(items);
            
            // If exactly 11 orders returned, remove the last one (used as "has more" indicator)
            if (ordersProcessed.length === 11) ordersProcessed.pop();
            
            // Update context with processed orders
            setCustomerOrders(ordersProcessed);
            
            // Set referrals data (promise3 result) - CRITICAL for Referral button
            setCustomerReferrals(
              values[1] !== undefined ? values[1].data.items : []
            );
            
            // Set notes data (promise4 result)
            setCustomerNotes(values[2] !== undefined ? values[2].data.items : []);
            
            // Set distributor level limit flag (promise5 result)
            setIsDistributorLevelLimit(values[3]?.data?.isDistributorLevelLimit);
            
            // All data loaded, hide loading indicator
            setApiChainInProgress(false);
          } catch {
            // If any error occurs during data processing, reset the entire context
            resetContext();
          }
        });
        
        // Exit early since we've handled the prefetch case
        // All necessary data will be set when the Promise.all resolves
        return;
      }

      sendRequest({
        method: "get",
        endpoint: `customers?unicity=${memberId}&expand=customer`,
      })
        .then((res) => {
          const member = res.data.items[0];
          setMemberData(member);

          const promise1 = sendRequest({
            method: "get",
            addPrefix: false,
            endpoint: `${res.data.items[0].href}?expand=profilePicture`,
            abortController,
          }).catch((error) => {
            setErrorMessage(error.message);
          });

          const promise2 = sendRequest({
            method: "get",
            addPrefix: false,
            endpoint: `${res.data.items[0].href}/orders?expand=order&limit=11&offset=${customerOrdersOffset}`,
            abortController,
          }).catch((error) => {
            setErrorMessage(error.message);
          });

          const promise3 = sendRequest({
            method: "get",
            addPrefix: false,
            endpoint: `${process.env.REACT_APP_OFFICE_API}/${userInfo.unicityId}/members?sponsorUnicityId=${memberId}`,
            abortController,
          }).catch((err) => console.log(err));

          const promise4 = sendRequest({
            method: "get",
            addPrefix: false,
            endpoint: `${process.env.REACT_APP_OFFICE_API}/${userInfo.unicityId}/notes?customerId=${memberId}`,
            abortController,
          }).catch((err) => console.log(err));

          const promise5 = sendRequest({
            method: "get",
            addPrefix: false,
            endpoint: `${process.env.REACT_APP_OFFICE_API}/${userInfo.unicityId}/distributorLevelLimit?partnerId=${memberId}`,
            abortController,
          }).catch((err) => console.log(err));

          const promise6 = sendRequest({
            method: "get",
            addPrefix: false,
            endpoint: `${process.env.REACT_APP_OFFICE_API}/${userInfo.unicityId}/members?searchTerm=${memberId}&limit=1&sortField=relevance&sortDirection=desc`,
            abortController,
          }).catch((err) => console.log(err));

          Promise.all([
            promise1,
            promise2,
            promise3,
            promise4,
            promise5,
            promise6,
          ]).then((values) => {
            try {
              setDistributorData(values[0].data);
              setCustomerHrefData(
                values[0] !== undefined ? values[0].data : false
              );
              setCustomerOrdersLength(values[5]?.data?.items[0]?.orders);

              const items = values[1] !== undefined ? values[1].data.items : [];
              // if there are less than 11 orders, set allOrdersShown to true
              if (items.length < 11) {
                setAllOrdersShown(true);
              }
              setCustomerOrdersInitialLength(items.length);
              // process and sort orders, pop off last order if there are 11
              const ordersProcessed = processAndSortOrders(items);
              if (ordersProcessed.length === 11) ordersProcessed.pop();
              setCustomerOrders(ordersProcessed);
              setCustomerReferrals(
                values[2] !== undefined ? values[2].data.items : []
              );
              setCustomerNotes(
                values[3] !== undefined ? values[3].data.items : []
              );

              setIsDistributorLevelLimit(
                values[4]?.data?.isDistributorLevelLimit
              );
              setApiChainInProgress(false);
            } catch {
              resetContext();
            }
          });
        })
        .catch((error) => {
          setErrorMessage(error.message);
        });
    }
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  // when customerOrdersOffset changes, make a call to get the next 10 orders
  useEffect(() => {
    const abortController = new AbortController();
    if (customerOrdersOffset) {
      setCustomerOrdersOffsetLoading(true);
      sendRequest({
        method: "get",
        endpoint: `customers?unicity=${memberId}&expand=customer`,
      }).then((res) => {
        sendRequest({
          method: "get",
          addPrefix: false,
          endpoint: `${res.data.items[0].href}/orders?expand=order&limit=11&offset=${customerOrdersOffset}`,
          abortController,
        })
          .then((res) => {
            const offsetOrders = res?.data?.items;
            if (offsetOrders?.length < 11) {
              setAllOrdersShown(true);
            } else offsetOrders.pop();
            const ordersAppended = customerOrders.concat(offsetOrders);
            const processedOrders = processAndSortOrders(ordersAppended);

            setCustomerOrders(processedOrders);
          })
          .catch((error) => {
            setErrorMessage(error.message);
          })
          .finally(() => {
            setCustomerOrdersOffsetLoading(false);
          });
      });
    }
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerOrdersOffset]);

  // when the drawer closes, clear out error message
  useEffect(() => {
    if (!isDrawerOpen) {
      setErrorMessage("");
    }
  }, [isDrawerOpen]);

  const resetContext = () => {
    setMemberId("");
    setMemberData({});
    setCustomerHrefData({});
    setCustomerOrders([]);
    setCustomerOrdersOffset(0);
    setCustomerReferrals([]);
    setCustomerNotes([]);
    setApiChainInProgress(true);
    setAllOrdersShown(false);
  };

  return (
    <MemberContext.Provider
      value={{
        memberData,
        setMemberData,
        customerHref,
        isDrawerOpen,
        setIsDrawerOpen,
        customerHrefData,
        setCustomerHrefData,
        apiChainInProgress,
        customerOrders,
        errorMessage,
        clearErrorMessage,
        customerReferrals,
        customerNotes,
        memberId,
        setMemberId,
        distributorData,
        resetContext,
        customerOrdersOffset,
        setCustomerOrdersOffset,
        allOrdersShown,
        setAllOrdersShown,
        customerOrdersOffsetLoading,
        customerOrdersInitialLength,
        isDistributorLevelLimit,
        setCustomerOrders,
        customerOrdersLength,
      }}
    >
      {props.children}
    </MemberContext.Provider>
  );
};

export { MemberContext, MemberContextProvider };
